import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

export type InvoiceStatus = 'NEW' | 'PENDING' | 'PAID' | 'CANCELED';
export type InvoiceGrouping = 'entry' | 'activity' | 'project' | 'person' | 'total';

export interface InvoiceLine {
  label: string;
  entryIds: string[];
  seconds: number;
  /** Minor units as a string. BigInt does not survive JSON. */
  amountMinor: string;
  from: string;
  to: string;
}

export interface InvoiceRow {
  id: string;
  number: string;
  customerId: string;
  customerName?: string;
  raisedBy?: string;
  from: string;
  to: string;
  issuedOn: string;
  dueOn: string;
  status: InvoiceStatus;
  paidOn: string | null;
  netMinor: string;
  taxMinor: string;
  grossMinor: string;
  taxBasisPoints: number;
  currency: string;
  billedSeconds: number;
  grouping: InvoiceGrouping;
  comment: string | null;
  createdAt: string;
  entryCount?: number;
}

export interface InvoiceDetail extends InvoiceRow {
  customer: { name: string; number: string | null; country: string | null; currency: string };
  organizationName: string;
  /** Who is issuing it. Any field may be empty, and an empty one is not printed. */
  issuer: {
    address: string;
    taxNumber: string;
    bankDetails: string;
    paymentTerms: string;
  };
  lines: InvoiceLine[];
}

export interface InvoicePreview {
  customer: { id: string; name: string; number: string | null; currency: string };
  from: string;
  to: string;
  grouping: InvoiceGrouping;
  lines: InvoiceLine[];
  entryCount: number;
  /** Entries with no rate. They are why a preview can be empty of money. */
  unpricedCount: number;
  billedSeconds: number;
  taxBasisPoints: number;
  netMinor: string;
  taxMinor: string;
  grossMinor: string;
  currency: string;
}

export interface InvoiceRequest {
  customerId: string;
  from: string;
  to: string;
  grouping: InvoiceGrouping;
  comment?: string | null;
}

export function useInvoices(filter: { customerId?: string; status?: InvoiceStatus } = {}) {
  const query = new URLSearchParams();
  if (filter.customerId) query.set('customerId', filter.customerId);
  if (filter.status) query.set('status', filter.status);
  const suffix = query.toString() ? `?${query}` : '';
  return useQuery({
    queryKey: ['invoices', filter],
    queryFn: () => api.get<InvoiceRow[]>(`/invoices${suffix}`),
  });
}

export function useInvoice(id: string | null) {
  return useQuery({
    queryKey: ['invoices', id],
    queryFn: () => api.get<InvoiceDetail>(`/invoices/${id}`),
    enabled: id !== null,
  });
}

/** A mutation rather than a query: it is asked for on demand, not on render. */
export function useInvoicePreview() {
  return useMutation({
    mutationFn: (body: InvoiceRequest) => api.post<InvoicePreview>('/invoices/preview', body),
  });
}

export function useRaiseInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: InvoiceRequest) => api.post<InvoiceRow>('/invoices', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      // Entries just became exported, so every screen that lists them is stale.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

export function useSetInvoiceStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: InvoiceStatus }) =>
      api.patch<InvoiceRow>(`/invoices/${id}/status`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

/** Minor units as a string to a plain decimal, without touching a float. */
export function money(minor: string, currency: string): string {
  const negative = minor.startsWith('-');
  const digits = (negative ? minor.slice(1) : minor).padStart(3, '0');
  const whole = digits.slice(0, -2);
  // Grouped in the Indian style, because the currency is rupees and 1,00,000 is
  // what a reader here expects to see.
  const grouped = whole.replace(/\B(?=(\d{2})+(?!\d))(?=\d{3}$|\d{3}\B)/g, ',');
  return `${negative ? '-' : ''}${currency} ${grouped}.${digits.slice(-2)}`;
}

export function hours(seconds: number): string {
  const sign = seconds < 0 ? '-' : '';
  const abs = Math.abs(seconds);
  return `${sign}${Math.floor(abs / 3600)}:${String(Math.floor((abs % 3600) / 60)).padStart(2, '0')}`;
}
